import { translate, getActiveLang } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import { isoDateToString } from '@openclassrooms/utils/date';

import { makeStyles } from '@mui/styles';

import CourseLicenses from './CourseLicenses';
import CourseTeachers from './CourseTeachers';
import CourseCreators from './CourseCreators';
import type { CourseTeacher, CourseCreator } from '../../types';

interface CourseCreditsProps {
  teachers: Array<CourseTeacher>;
  creators: Array<CourseCreator>;
  license: string;
  updatedAt: string;
}

const useStyles = makeStyles(({ spacing, palette, breakpoints }) => ({
  root: {
    margin: spacing(5, 0),

    [breakpoints.up('sm')]: {
      margin: spacing(10, 0),
    },
  },
  title: {
    borderBottom: `1px solid ${palette.line}`,
    margin: spacing(5, 0, 3, 0),
    paddingBottom: spacing(1),
  },
  creatorsContentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: spacing(2),

    [breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginTop: spacing(3),
    },
  },
  teachersWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: spacing(3),

    [breakpoints.up('sm')]: {
      marginTop: spacing(5),
    },
  },
  textContent: {
    display: 'flex',
    marginLeft: spacing(2),
    alignItems: 'center',
  },
  creatorWrapper: {
    marginTop: spacing(6),

    [breakpoints.up('sm')]: {
      marginTop: spacing(9),
    },
  },
  secondaryInformationWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  secondaryInformation: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing(2),

    [breakpoints.up('sm')]: {
      marginTop: 0,

      '&:not(:first-of-type)': {
        marginTop: spacing(2),
      },
    },
  },
  secondaryInformationLabel: {
    marginRight: spacing(2),
  },
  separator: {
    width: 1,
    backgroundColor: palette.line,
    margin: spacing(0, 6),
  },
}));

const CourseCredits = ({ teachers, creators, license, updatedAt }: CourseCreditsProps) => {
  const classes = useStyles();

  const activeLang = getActiveLang();

  const updatedAtString = isoDateToString(updatedAt, activeLang, {
    day: activeLang === 'fr' ? '2-digit' : 'numeric',
    month: activeLang === 'fr' ? '2-digit' : 'numeric',
    year: 'numeric',
  });

  return (
    <div className={classes.root}>
      <ContentWrapper>
        <Typography className={classes.title} variant="h3" component="h2">
          {translate('course.credits.title')}
        </Typography>

        <div className={classes.teachersWrapper}>
          <Typography variant="h5" component="h3">
            {translate('course.teachers.title', {}, teachers.length)}
          </Typography>

          <CourseTeachers teachers={teachers} />
        </div>

        <div className={classes.creatorWrapper}>
          <Typography variant="h5" component="h3">
            {translate('course.teachers.course_creators.title')}
          </Typography>

          <div className={classes.creatorsContentWrapper}>
            <CourseCreators creators={creators} />

            <div className={classes.separator} />

            <div className={classes.secondaryInformationWrapper}>
              <div className={classes.secondaryInformation}>
                <Typography
                  variant="subheading"
                  className={classes.secondaryInformationLabel}
                  component="p"
                >
                  {translate('course.last_update.label', { date: updatedAtString })}
                </Typography>
              </div>
              <div className={classes.secondaryInformation}>
                <Typography
                  variant="subheading"
                  className={classes.secondaryInformationLabel}
                  component="p"
                >
                  {translate('course.license.label')}
                </Typography>

                <CourseLicenses license={license} />
              </div>
            </div>
          </div>
        </div>
      </ContentWrapper>
    </div>
  );
};

export default CourseCredits;
